// Function to load Google Maps API dynamically
const loadGoogleMapsApi = (): Promise<void> => {
  const CALLBACK_NAME = 'initGoogleMapsApi';
  const TIMEOUT_MS = 10000; // 10 seconds timeout for loading

  console.log('loadGoogleMapsApi (callback method): Starting.');

  return new Promise<void>((resolve, reject) => {
    // Check if the API is already loaded or initialization is in progress
    if (window.google && window.google.maps && window.google.maps.places) {
      console.log('loadGoogleMapsApi: Already loaded.');
      resolve();
      return;
    }

    // Check if the callback is already defined (means loading is in progress)
    if (typeof window[CALLBACK_NAME] === 'function') {
       console.log('loadGoogleMapsApi: Loading already in progress.');
       // Potentially add logic here to wait for the existing promise if needed,
       // but for now, let's rely on the initial check.
       // This scenario shouldn't usually happen with the context provider structure.
       // Rejecting might be safer to avoid duplicate states.
       reject(new Error('Google Maps API loading seems to be already in progress.'));
       return;
    }

    // --- Setup Timeout ---
    let timeoutId: NodeJS.Timeout | null = null;
    const cleanup = () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
      // Remove the global callback function once done
      delete window[CALLBACK_NAME];
      // Remove the script tag if it exists and loading failed/timed out
      const scriptElement = document.getElementById('google-maps-script');
      if (scriptElement) {
         // scriptElement.remove(); // Optional: clean up script tag on failure/timeout
      }
    };

    timeoutId = setTimeout(() => {
      console.error(`loadGoogleMapsApi: Loading timed out after ${TIMEOUT_MS}ms.`);
      cleanup();
      reject(new Error(`Google Maps API failed to load within ${TIMEOUT_MS}ms`));
    }, TIMEOUT_MS);

    // --- Define the global callback ---
    window[CALLBACK_NAME] = () => {
      console.log(`loadGoogleMapsApi: Global callback ${CALLBACK_NAME} executed.`);
      if (window.google && window.google.maps && window.google.maps.places) {
         console.log('loadGoogleMapsApi: API ready via callback, resolving promise.');
         cleanup();
         resolve();
      } else {
         console.error('loadGoogleMapsApi: Callback executed but places library not available.');
         cleanup();
         reject(new Error('Google Maps API callback executed but places library not available'));
      }
    };

    // --- Create and append the script ---
    // Check if script tag exists already (belt-and-suspenders check)
    if (document.getElementById('google-maps-script')) {
      console.warn('loadGoogleMapsApi: Script element already exists before appending.');
      // Don't reject here, rely on the timeout or the existing script's callback
      return;
    }

    console.log('loadGoogleMapsApi: Creating script element with callback.');
    const script = document.createElement('script');
    script.id = 'google-maps-script';
    // IMPORTANT: Add the callback parameter to the URL
    script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}&libraries=places&language=en&callback=${CALLBACK_NAME}`;
    script.async = true;
    // We no longer need script.onload

    script.onerror = (event) => {
      // onerror might still work, worth keeping for network-level errors
      console.error('loadGoogleMapsApi: script.onerror triggered.', event);
      cleanup();
      reject(new Error('Google Maps API script failed to load (onerror event)'));
    };

    console.log('loadGoogleMapsApi: Appending script with callback to head.');
    document.head.appendChild(script);
  });
};

export default loadGoogleMapsApi;